'use client'

import { useState, useRef } from 'react'
import { Upload, X, FileText, CheckCircle2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface FileUploadProps {
  onUpload: (fileUrl: string, fileName: string) => void
  value?: string
  accept?: string
  maxSize?: number // MB
  className?: string
  forRegistration?: boolean // 是否为注册用途（允许未登录用户上传）
}

export function FileUpload({ 
  onUpload, 
  value, 
  accept = '*/*',
  maxSize = 10,
  className,
  forRegistration = false
}: FileUploadProps) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [uploadedFile, setUploadedFile] = useState<{ name: string; url: string } | null>(
    value ? { name: '已上传文件', url: value } : null
  )
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // 验证文件大小
    if (file.size > maxSize * 1024 * 1024) {
      setError(`文件大小不能超过${maxSize}MB`)
      return
    }

    setError('')
    setUploading(true)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await fetch(`/api/upload${forRegistration ? '?for=registration' : ''}`, {
        method: 'POST',
        body: formData,
        credentials: 'include' // 确保携带 Cookie
      })

      const data = await response.json()

      if (data.success) {
        setUploadedFile({ name: data.fileName, url: data.fileUrl })
        onUpload(data.fileUrl, data.fileName)
      } else {
        setError(data.message || '上传失败')
        console.error('上传失败详情:', data)
      }
    } catch (err) {
      setError('上传失败，请重试')
      console.error('上传错误:', err)
    } finally {
      setUploading(false)
      if (inputRef.current) {
        inputRef.current.value = ''
      }
    }
  }

  const handleRemove = () => {
    setUploadedFile(null)
    onUpload('', '')
  }

  const handleClick = () => {
    inputRef.current?.click()
  }

  return (
    <div className={cn("space-y-2", className)}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
      />
      
      {uploadedFile ? (
        <div className={cn(
          "flex items-center gap-3 bg-muted rounded-lg",
          className?.includes('compact') ? "p-2" : "p-3"
        )}>
          <FileText className={cn(
            "text-primary",
            className?.includes('compact') ? "w-5 h-5" : "w-8 h-8"
          )} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{uploadedFile.name}</p>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3 text-primary" />
              上传成功
            </p>
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="p-1 hover:bg-background rounded"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      ) : (
        <div
          onClick={handleClick}
          className={cn(
            "border-2 border-dashed rounded-lg text-center transition-colors cursor-pointer",
            className?.includes('compact') ? "p-2" : "p-4",
            uploading ? "border-primary/50 bg-primary/5" : "hover:border-primary/50"
          )}
        >
          {uploading ? (
            <>
              <div className={cn(
                "mx-auto border-2 border-primary border-t-transparent rounded-full animate-spin",
                className?.includes('compact') ? "w-4 h-4 mb-1" : "w-6 h-6 mb-2"
              )} />
              <p className="text-sm text-muted-foreground">上传中...</p>
            </>
          ) : (
            <>
              <Upload className={cn(
                "mx-auto text-muted-foreground",
                className?.includes('compact') ? "w-4 h-4 mb-1" : "w-6 h-6 mb-2"
              )} />
              <p className="text-sm text-muted-foreground">点击或拖拽上传文件</p>
              {!className?.includes('compact') && (
                <p className="text-xs text-muted-foreground mt-1">最大 {maxSize}MB</p>
              )}
            </>
          )}
        </div>
      )}

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  )
}
